import React from 'react';
import styled, { css } from 'styled-components';

import { ISimejiItem } from '@template/Simeji/simejiStatus.interface';
import { STATUS_TYPE, STATUS_TYPE_VALUE } from '@template/Simeji/simejiStatus';

import {
  Eyes,
  Hands,
  SmileEyes,
  CloseEyes,
  Cloth,
  EyeBrow,
  Mouth,
  Eyelids,
  Hair,
  BackHair,
  Face,
  Legs,
  Balloon,
  Acc,
  Acc2,
  Food,
} from './draw';
import SleepLeg from './draw/SleepLeg';

const sideStyle = css`
  .hair-front,
  .face-front {
    display: none;
  }

  .hair-side,
  .face-side {
    display: block;
  }

  .eyelid-left,
  .eyebrow-left {
    transform: matrix(-1, 0, 0, 1, 22, 0);
  }

  .eyelid-right,
  .eyebrow-right {
    transform: translateX(-2px);
  }
  .eyes {
    transform: translateX(-4px);
  }
  .left-eye {
    transform: translateX(2px);
  }
  .left-ear,
  .left-acc {
    display: none;
  }
`;

const typeMap = {
  [STATUS_TYPE.DEFAULT]: css`
    .body,
    .cloth,
    .leg-cloth {
      animation: 0.5s linear infinite alternate running breath;
    }

    .eyelid-right {
      display: block;
      animation: 2s linear infinite alternate rightEyeblink;
    }
    .eyelid-left {
      display: block;
      animation: 2s linear infinite alternate leftEyeblink;
    }
  `,
  [STATUS_TYPE.SLEEP]: css`
    .eyelid,
    .left-leg,
    .right-leg,
    .eyes {
      display: none;
    }

    .hair-back {
      transform: translateY(-8px);
    }

    .close-eye,
    .sleep-leg {
      display: block;
    }

    .body,
    .cloth {
      animation: 1s linear infinite alternate running sleep;
    }

    .left-balloon {
      transform-origin: center center;
      display: block;
      animation: 1s linear infinite alternate running sleepBalloon;
    }

    .left-hand,
    .right-hand {
      transform-origin: top center;
    }

    .left-hand {
      transform: scaleX(-1) translateX(-14px) translateY(0px) rotate(-22deg);
    }
    .right-hand {
      transform: scaleX(-1) translateX(10px) translateY(0px) rotate(22deg);
    }

    @keyframes sleepBalloon {
      0% {
        transform: scale(0.9) translateY(10px);
      }
      100% {
        transform: scale(1) translateY(10px);
      }
    }

    @keyframes sleep {
      0% {
        transform: translateY(13px);
      }
      100% {
        transform: translateY(10px);
      }
    }
  `,

  [STATUS_TYPE.MOVE]: css`
    .hair-front,
    .face-front {
      display: block;
    }

    .hair-side,
    .face-side {
      display: none;
    }

    transform-origin: top center;
    animation: 1s ease-in-out infinite alternate running shake;

    .eyelid,
    .legs,
    .eyes {
      display: block;
    }

    .close-eye,
    .sleep-leg {
      display: none;
    }
    .hair-back {
      transform: translateY(-8px);
    }

    .legs {
      transform: translateY(-10px);
    }

    .cloth {
      transform: scale(1, 0.95);
    }

    .right-balloon {
      transform-origin: center center;
      opacity: 1;
      animation: 1s linear infinite alternate running breathBalloon;
    }

    .left-hand,
    .right-hand {
      transform-origin: top center;
    }

    .left-hand {
      transform: scaleX(-1) translateX(-14px) translateY(-2px) rotate(-22deg);
    }
    .right-hand {
      transform: scaleX(-1) translateX(10px) translateY(-2px) rotate(22deg);
    }

    @keyframes shake {
      0% {
        transform: rotate(0.01turn);
      }
      100% {
        transform: rotate(-0.01turn);
      }
    }
  `,
  [STATUS_TYPE.WALK]: css`
    ${sideStyle}

    .body,
    .cloth {
      animation: 0.5s linear infinite alternate running breath;
    }

    .left-hand,
    .right-hand,
    .left-leg,
    .right-leg {
      transform-origin: center center;
    }

    .left-leg {
      animation: 0.5s linear 0.2s infinite alternate running legs-shake;
    }
    .right-leg {
      animation: 0.5s linear infinite alternate running legs-shake;
    }

    .hair-back {
      transform: translateX(2px);
    }

    @keyframes legs-shake {
      0% {
        transform: translateY(0px) rotate(1deg);
      }
      100% {
        transform: translateY(-2px) rotate(-1deg);
      }
    }
  `,
  [STATUS_TYPE.CLICK]: css`
    .eyelid,
    .eyes {
      display: none;
    }

    .smile {
      display: block;
    }

    .body {
      transform: translateY(2px);
    }
  `,
  [STATUS_TYPE.CLIMB]: css`
    .left-hand,
    .left-leg {
      display: none;
    }
    .right-hand {
      transform-origin: top center;
      transform: scaleX(-1) translateX(-72px) translateY(34px) rotate(-45deg);
    }
    .right-leg {
      transform-origin: top center;
      transform: scaleX(-1) translateX(-106px) translateY(100px) rotate(-90deg);
    }

    ${sideStyle}
  `,
  [STATUS_TYPE.EAT]: css`
    .eyelid,
    .left-leg,
    .right-leg,
    .eyes {
      display: none;
    }

    .hair-back {
      transform: translateY(-8px);
    }

    .smile,
    .sleep-leg,
    .food {
      display: block;
    }

    .body,
    .cloth {
      animation: 0.2s linear infinite alternate running eat;
    }

    .left-hand,
    .right-hand {
      transform-origin: top center;
    }

    .left-hand {
      transform: scaleX(-1) translateX(4px) translateY(0px) rotate(-12deg);
    }
    .right-hand {
      transform: scaleX(-1) translateX(-6px) translateY(0px) rotate(12deg);
    }

    @keyframes eat {
      0% {
        transform: translateY(11px);
      }
      100% {
        transform: translateY(10px);
      }
    }
  `,
};

const resetStyle = css`
  .sleep-leg {
    display: none;
    position: absolute;
    bottom: 0;
    left: 0;
  }
  .close-eye,
  .smile,
  .left-balloon,
  .right-balloon,
  .hair-side,
  .face-side,
  .food {
    display: none;
  }
`;

const dirMap = {
  '0': css`
    transform: rotateY(0deg);
  `,
  '1': css`
    transform: rotateY(180deg);
  `,
  '-1': css`
    transform: rotateY(0deg);
  `,
};

const SvgBodyBox = styled.div<{
  customType: STATUS_TYPE_VALUE;
  data: ISimejiItem;
  dir: number | string;
}>`
  transition: all 0.2s;
  position: relative;

  .eyelid-left,
  .eyebrow-left {
    transform: matrix(-1, 0, 0, 1, 25, 0);
  }

  ${resetStyle};
  ${({ customType }) => typeMap[customType]};
  ${({ dir }) => dirMap[dir]};

  @keyframes rightEyeblink {
    10% {
      transform: translateY(0);
    }
    15% {
      transform: translateY(8px);
    }
    20% {
      transform: translateY(0);
    }
    100% {
    }
  }

  @keyframes leftEyeblink {
    10% {
      transform: matrix(-1, 0, 0, 1, 25, 0) translateY(0);
    }
    15% {
      transform: matrix(-1, 0, 0, 1, 25, 0) translateY(8px);
    }
    20% {
      transform: matrix(-1, 0, 0, 1, 25, 0) translateY(0);
    }
    100% {
      transform: matrix(-1, 0, 0, 1, 25, 0);
    }
  }

  @keyframes breath {
    0% {
      transform: translateY(1px);
    }
    100% {
      transform: translateY(-1px);
    }
  }
`;

type Props = {
  data: ISimejiItem;
  dir?: number;
  type?: STATUS_TYPE_VALUE;
};

function DrawBody({ data, dir, type }: Props) {
  const {
    lineColor,
    skinColor,
    hairColor,
    doubleEyelidColor,
    cheekColor,
    closeEyes,
    smileEyes,
    mouth,
    outerClothColor,
  } = data;

  return (
    <SvgBodyBox
      customType={type || STATUS_TYPE.DEFAULT}
      dir={`${dir || 0}`}
      data={data}
    >
      {/* 뒷 헤어 */}
      <BackHair data={data} />

      {/* 다리 */}
      <Legs skinColor={skinColor} data={data} />

      {/* 의상 */}
      <Cloth data={data} color={hairColor} />

      {/* 앉은 발 */}
      {/* 이 발 위치 때문에 항상 다리-옷-앉은발 */}
      <SleepLeg data={data} />

      {/* 팔 */}
      <Hands
        skinColor={skinColor}
        lineColor={lineColor}
        color={outerClothColor}
        data={data}
      />
      <svg
        width="100"
        height="120"
        viewBox="0 0 100 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        color={data.lineColor}
      >
        {/* 바디 */}
        <g className="body">
          {/* 얼굴 */}
          <Face
            color={lineColor}
            skinColor={skinColor}
            cheekColor={cheekColor}
          />

          {/* 입 */}
          <Mouth icon={mouth} color={lineColor} />

          {/* 스마일 */}
          <SmileEyes
            icon={smileEyes}
            color={lineColor}
            eyelidColor={doubleEyelidColor}
          />

          {/* 눈 감음 */}
          <CloseEyes
            icon={closeEyes}
            color={lineColor}
            eyelidColor={doubleEyelidColor}
          />

          {/* 눈 */}
          <Eyes data={data} />

          {/* 눈 두덩이 + 커풀 */}
          <Eyelids
            data={data}
            skinColor={skinColor}
            color={lineColor}
            eyelidColor={doubleEyelidColor}
          />
        </g>

        {/* body 끝 */}
        <defs>
          <clipPath id="clip0_3465_6369">
            <rect
              width="26"
              height="25"
              fill="white"
              transform="translate(37.5 85.5)"
            />
          </clipPath>
        </defs>
      </svg>

      {/* 헤어 */}
      <Hair data={data} color={hairColor} />
      {/* 음식 */}
      <Food data={data.food} />
      {/* 악세사리 */}
      <Acc data={data} />
      <Balloon data={data} />
    </SvgBodyBox>
  );
}

export default DrawBody;
